import { Button, LinearProgressBar, Text } from '@wix/design-system'
import { useContent, useHistoryEntries, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import React, { FC, useEffect, useState } from 'react'

import { createHeadlessMetaSite } from '../../../api/HeadlessBusinessSetupService/createHeadlessMetaSite'
import { provisionHeadlessMetaSite } from '../../../api/provisionHeadlessMetaSite'
import { usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useErrorNotification } from '../../../components/ErrorNotification/ErrorNotificationContext'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { easeInOutCubic } from '../../WixIntro/Transition/Transition'
import { Content } from './Content'
import s from './HeadlessMobileSiteCreation.scss'

const PROGRESS_TICK_MS = 150

// Each phase eases towards its ceiling over the time its request usually takes, and holds
// there until the request actually resolves - the bar never fills up while work is left
const CREATING_METASITE = { from: 0, to: 40, durationMs: 8000 }
const PROVISIONING = { from: 40, to: 90, durationMs: 6000 }

export const HeadlessMobileSiteCreation: FC = () => {
  const { content } = useContent<Content>()
  const next = useNext()
  const { showError } = useErrorNotification()
  const pageLogger = usePageLogger()
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const { save, businessName, category, appDefIds = [], provisionedMetaSiteId } = useStorageContext()
  const [historyEntries] = useHistoryEntries()
  const [failed, setFailed] = useState(false)
  const [progress, setProgress] = useState(0)
  const [phase, setPhase] = useState(CREATING_METASITE)

  // An empty history means the page was rendered without a flow context (BO preview, dev),
  // where creating a site would be a surprise
  const isFlowRun = historyEntries.length > 0

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (!isFlowRun) {
      return
    }

    const startedAt = Date.now()
    const interval = setInterval(() => {
      const elapsed = Math.min((Date.now() - startedAt) / phase.durationMs, 1)
      setProgress(phase.from + (phase.to - phase.from) * easeInOutCubic(elapsed))
    }, PROGRESS_TICK_MS)

    return () => clearInterval(interval)
  }, [phase, isFlowRun])

  const provisionAndContinue = async () => {
    setFailed(false)

    const failProvisioning = (error: unknown) => {
      console.error('Failed to provision headless meta site:', error)
      void pageLogger.generalAction({ action: 'provision_failed' })
      setFailed(true)
      showError()
    }

    // The stored id means the metasite was already created (previous attempt / back navigation) —
    // never create a second project
    let metaSiteId = provisionedMetaSiteId

    if (!metaSiteId) {
      const [createError, createResponse] = await tryCatch(() => createHeadlessMetaSite({ origin: flowBiSlug }))
      metaSiteId = createResponse?.data.metaSiteId || undefined
      if (createError || !metaSiteId) {
        failProvisioning(createError)
        return
      }
      save({ provisionedMetaSiteId: metaSiteId })
    }

    setPhase(PROVISIONING)

    const { industryId, structureId, displayName, postRegVertical, businessTermId, businessSegmentId } =
      resolveCategoryFromBusinessTerm(category?.businessTerm, category?.businessSegmentId)

    const [provisionError] = await tryCatch(() =>
      provisionHeadlessMetaSite({
        metaSiteId: metaSiteId!,
        origin: flowBiSlug,
        businessName,
        category: {
          industryId,
          structureId,
          displayName,
          postRegVertical,
          businessTermId,
          businessSegmentId,
        },
        selectedApps: appDefIds.map(appDefId => ({ appDefId })),
      }),
    )
    if (provisionError) {
      failProvisioning(provisionError)
      return
    }

    setProgress(100)

    // The metadata write is what resolves {metadata_metaSiteId} in the flow's external transition URL
    next({}, { metaSiteId })
  }

  useEffect(() => {
    if (!isFlowRun) {
      void pageLogger.generalAction({ action: 'provision_skipped' })
      return
    }
    void provisionAndContinue()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onRetryClick = () => {
    void pageLogger.generalAction({ action: 'provision_retry' })
    void provisionAndContinue()
  }

  return (
    <div className={s.page}>
      <div className={s.content}>
        <img
          className={s.logo}
          src={content.thumbnail.content.src}
          alt={content.thumbnail.content.alt}
          width={content.thumbnail.content.width}
          height={content.thumbnail.content.height}
        />
        {content.title && <Text className={s.title}>{content.title}</Text>}
        {!failed && <LinearProgressBar value={progress} />}
        {failed && (
          <Button className={s.retryButton} onClick={onRetryClick}>
            {content.retryBtn}
          </Button>
        )}
      </div>
    </div>
  )
}
